/**
 * Create Deal Handler
 * Edit handler for creating new deals
 */

import type { ToolExecutionContext } from '../../types/index.js';
import type { EditPreview } from '../../types/index.js';
import { EditHandlerBase } from '../base/index.js';
import { uuidField, objectField } from '../../utils/schema-builder.js';
import { generateConfirmationToken } from '../../utils/confirmation-token.js';
import { createUserGraphQLClient } from '../../utils/graphql-client.js';
import { validateCreateDealInput, validateCompanyId } from '../../utils/deal-validation.js';

/**
 * Create deal handler
 * Generates preview with confirmation token for new deal creation
 */
export class CreateDealHandler extends EditHandlerBase<any> {
  id = 'create_deal';

  schema = {
    type: 'object',
    properties: {
      company_id: uuidField('UUID of the company for this deal'),
      title: {
        type: 'string',
        description: 'Deal title/name',
      },
      description: {
        type: 'string',
        description: 'Deal description (optional)',
      },
      amount: {
        type: 'number',
        description:
          'Deal amount in currency units (optional). Omit when the user did not state an amount — NEVER invent a placeholder value like 1.',
      },
      assigned_users: {
        type: 'array',
        items: { type: 'string' },
        description:
          "User ids of the deal's owners/assignees (optional). Resolve each via find_employee first — these are Hub app_users ids (uuids), NOT contact ids.",
      },
      currency: {
        type: 'string',
        description: 'Currency code (default: USD)',
        default: 'USD',
      },
      stage: {
        type: 'string',
        enum: ['lead', 'qualified', 'discovery', 'proposal', 'negotiation', 'contract', 'won', 'lost'],
        description: 'Initial deal stage (default: lead)',
        default: 'lead',
      },
      close_date: {
        type: 'string',
        description: 'Expected close date (ISO format, optional)',
      },
      close_probability: {
        type: 'number',
        description: 'Estimated probability of closing (0-100, default: 50)',
        default: 50,
      },
      is_recurring: {
        type: 'boolean',
        description: 'Whether this is a recurring revenue deal (default: false)',
        default: false,
      },
      monthly_amount: {
        type: 'number',
        description: 'Monthly recurring revenue amount if is_recurring is true (optional)',
      },
      is_committed: {
        type: 'boolean',
        description: 'Whether revenue is committed (default: false)',
        default: false,
      },
      tags: {
        type: 'array',
        items: { type: 'string' },
        description: 'Tags for categorizing the deal (optional)',
      },
      notes: {
        type: 'string',
        description: 'Additional notes about the deal (optional)',
      },
    },
    required: ['company_id', 'title'],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<EditPreview> {
    // Validate input
    const validatedInput = validateCreateDealInput(params);
    const { company_id, title, amount } = validatedInput;

    if (!context?.userToken) {
      throw new Error('User token is required for deal creation');
    }

    try {
      const { GetCompanyDocument } = await import('@/src/gql/graphql');
      const client = createUserGraphQLClient(context.userToken);

      // Verify company exists
      const companyResult = await client.query({
        query: GetCompanyDocument,
        variables: {
          id: company_id,
        },
        fetchPolicy: 'network-only',
      });

      if (companyResult.error) {
        throw new Error(`GraphQL error: ${companyResult.error.message}`);
      }

      const companyData = companyResult.data as any;
      const company = companyData?.companiesCollection?.edges?.[0]?.node;

      if (!company) {
        throw new Error(`Company with ID "${company_id}" not found or you don't have permission to access it`);
      }

      // Build the deal preview
      const dealPreview = {
        id: 'new-deal-preview',
        company_id,
        company_name: company.name,
        title,
        description: params.description || null,
        amount: amount ?? null,
        assigned_users: params.assigned_users || [],
        currency: params.currency || 'USD',
        stage: params.stage || 'lead',
        close_date: params.close_date || null,
        close_probability: params.close_probability ?? 50,
        is_recurring: params.is_recurring ?? false,
        monthly_amount: params.monthly_amount || null,
        is_committed: params.is_committed ?? false,
        tags: params.tags || [],
        notes: params.notes || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Generate confirmation token
      const token = generateConfirmationToken({
        entityType: 'deal',
        entityId: 'new',
        changes: params,
        userId: context?.userId || 'system',
        chatId: context?.chatId || 'system',
        messageId: context?.messageId || 'system',
      });

      // Generate changes display
      const changes: Record<string, any> = {
        title: { old: null, new: title, type: 'added' },
        company: { old: null, new: company.name, type: 'added' },
        stage: { old: null, new: params.stage || 'lead', type: 'added' },
      };

      // amount is optional — only surface it when the user actually stated one
      // (never invent a placeholder).
      if (amount !== undefined && amount !== null) {
        changes.amount = { old: null, new: amount, type: 'added' };
      }
      if (params.assigned_users && params.assigned_users.length > 0) {
        changes.assigned_users = { old: null, new: params.assigned_users, type: 'added' };
      }
      if (params.description) {
        changes.description = { old: null, new: params.description, type: 'added' };
      }
      if (params.close_date) {
        changes.close_date = { old: null, new: params.close_date, type: 'added' };
      }
      if (params.close_probability && params.close_probability !== 50) {
        changes.close_probability = { old: null, new: params.close_probability, type: 'added' };
      }
      if (params.is_recurring) {
        changes.is_recurring = { old: null, new: true, type: 'added' };
        if (params.monthly_amount) {
          changes.monthly_amount = { old: null, new: params.monthly_amount, type: 'added' };
        }
      }
      if (params.tags && params.tags.length > 0) {
        changes.tags = { old: null, new: params.tags, type: 'added' };
      }

      return {
        type: 'preview',
        entity: 'deal',
        id: 'new',
        changes,
        confirmationToken: token,
        preview: dealPreview,
      };
    } catch (error) {
      throw new Error(`Deal creation preview failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
